import { useEffect, useState, useRef } from "react";
import ReactSelect from "react-select";
import Swal from "sweetalert2";

// componentes
import EstadoBadge from "../components/EstadoBadge";
import Loader from "../components/Loader";

// conexiones a la API
import { listIngresosLazy, AddIngresos } from "../api/ingresos";
import { listTipoDocumento002, listDepartamento012, listMunicipio013, listActividadEconomica19 } from "../api/hacienda"
import { listProductosLazy } from "../api/productos";
import { listProveedor, createProveedor } from "../api/proveedores";

// utilidades
import { DataIngreso } from '../utils/validaciones';
import { formatDateReadable } from '../utils/fechas';
import { listProductos, addProductoRelacionado } from "../api/productos";

const today = new Date().toISOString().split('T')[0];

const Ingresos = () => {
  const [loading, setLoading] = useState(true);
  const [ingresos, setIngresos] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [buscar, setBuscar] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Modal principal
  const [showModal, setShowModal] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  const [fechaIngreso, setFechaIngreso] = useState(today);
  const [numeroFactura, setNumeroFactura] = useState("");
  const [codigoFactura, setCodigoFactura] = useState("");
  const [tipoFactura, setTipoFactura] = useState("");
  const [iva, setIva] = useState(0);

  // Modal de productos
  const [showProductoModal, setShowProductoModal] = useState(false);
  const [productos, setProductos] = useState([]);
  const [buscarProducto, setBuscarProducto] = useState("");
  const [pagePro, setPagePro] = useState(1);
  const [totalPagesPro, setTotalPagesPro] = useState(1);
  const [limitPro] = useState(10);

  const [tipoDocumento, setTipoDocumento] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const fileInputRef = useRef(null);

  // Estados para Modal de Proveedor (Importación JSON)
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [actividad, setActividadEconomica] = useState([]);
  const [pendingJsonImport, setPendingJsonImport] = useState(null);
  const [rawJsonString, setRawJsonString] = useState(null);
  const [formProveedor, setFormProveedor] = useState({
    name: "",
    nombre_comercial: "",
    nit: "",
    dui: "00000000-0", // Default as per original spec if not provided
    nrc: "",
    actividad: "",
    departamento: "",
    municipio: "",
    direccion: "",
    telefono: "",
    correo: "",
    status: "1",
  });

  const [form, setForm] = useState({
  });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIngreso, setSelectedIngreso] = useState(null);

  const resetForm = () => {
    setProveedorSeleccionado(null);
    setProductosSeleccionados([]);
    setFechaIngreso(today);
    setNumeroFactura("");
    setCodigoFactura("");
    setTipoFactura("");
    setIva(0);
    setForm({});
    setPendingJsonImport(null);
    setRawJsonString(null);
    setEditMode(false);
    setSelectedIngreso(null);
  };

  const fetchData = async (p = 1) => {
    try {
      setEditMode(false);
      setLoading(true);
      const data = await listIngresosLazy(p, limit, buscar);
      setIngresos(data.data);
      setTotalPages(data.totalPages);
      setPage(data.currentPage);

      const doc = await listTipoDocumento002();
      setTipoDocumento(doc.data);

    } catch (error) {
      console.error("Error al obtener los ingresos", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProveedores = async () => {
    try {
      const data = await listProveedor();
      const provOpts = data.data.map(p => ({ value: p.id, label: p.name, nit: p.nit, nrc: p.nrc }));
      setProveedores(provOpts);
      return provOpts;
    } catch (error) {
      console.error("Error al obtener proveedores", error);
      return [];
    }
  };

  const fetchProviderData = async () => {
    try {
      if (departamentos.length === 0) {
        const [dep, mun, act] = await Promise.all([
          listDepartamento012({}),
          listMunicipio013({}),
          listActividadEconomica19({})
        ]);
        setDepartamentos(dep.data || []);
        setMunicipios(mun.data || []);
        setActividadEconomica(act.data || []);

        return { depData: dep.data || [], munData: mun.data || [], actData: act.data || [] };
      }
      return { depData: departamentos, munData: municipios, actData: actividad };
    } catch (error) {
      console.error("Error fetching provider data", error);
      return { depData: [], munData: [], actData: [] };
    }
  };

  const fetchProductos = async (p = 1) => {
    try {
      const data = await listProductosLazy(p, limitPro, buscarProducto);
      setProductos(data.data);
      setTotalPagesPro(data.totalPages);
      setPagePro(data.currentPage);
    } catch (error) {
      console.error("Error al obtener productos", error);
    }
  };

  const fetchAllProductsForMatching = async () => {
    try {
      const data = await listProductos({});
      if (data && data.data) {
        setAllProducts(data.data);
      }
    } catch (error) {
      console.error("Error fetching all products", error);
    }
  };

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line 
  }, [page, limit, buscar]);

  useEffect(() => {
    if (showProductoModal) {
      fetchProductos(pagePro);
    }
    // eslint-disable-next-line 
  }, [showProductoModal, pagePro, buscarProducto]);

  const handleAddProducto = (producto) => {
    const newProd = {
      value: producto.id,
      label: `${producto.sku} | ${producto.name}`,
      cantidad: 1,
      precio: producto.price,
    };
    const exists = productosSeleccionados.find(p => p.value === newProd.value);
    if (!exists) {
      setProductosSeleccionados([...productosSeleccionados, newProd]);
    }
  };

  const handleChangeCantidad = (index, cantidad) => {
    const updated = [...productosSeleccionados];
    updated[index].cantidad = cantidad;
    setProductosSeleccionados(updated);
  };

  const handleChangePrecio = (index, precio) => {
    const updated = [...productosSeleccionados];
    updated[index].precio = precio;
    setProductosSeleccionados(updated);
  };

  const handleSelectChange = (value, name) => {
    if (value) {
      setTipoFactura(value.value);
      setForm((prev) => ({ ...prev, [name]: value.value }));
    } else {
      setTipoFactura(null);
      setForm((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    resetForm();

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const jsonContent = event.target.result;
        const json = JSON.parse(jsonContent);
        setRawJsonString(jsonContent);

        // 1. Extraer cabecera
        const { fecEmi, numeroControl, codigoGeneracion, tipoDte } = json.identificacion || {};
        const nitEmisor = json.emisor?.nit;

        setFechaIngreso(fecEmi || "");
        setNumeroFactura(numeroControl || "");
        setCodigoFactura(codigoGeneracion || "");

        // Validar que el tipo de documento sea válido para ingresos (01, 03 o 88)
        const tipoDteStr = String(tipoDte).padStart(2, '0');
        if (!["01", "03", "88"].includes(tipoDteStr)) {
          Swal.fire({
            icon: 'error',
            title: 'Tipo de Documento no permitido',
            text: `El JSON cargado es de tipo ${tipoDteStr}. Solo se permiten: Factura (01), Comprobante de Crédito Fiscal (03) y Ticket (88) para los ingresos.`,
          });
          e.target.value = null;
          return;
        }

        // 1.5. Asignar el tipo de documento en base al DTE
        if (tipoDte && tipoDocumento && tipoDocumento.length > 0) {
          const matchedDoc = tipoDocumento.find(c => {
            const doc = c.typeFact || c;
            // Evaluamos el string (asegurando ceros a la izquierda ej: "01")
            return doc.codigo === String(tipoDte).padStart(2, '0');
          });
          if (matchedDoc) {
            const docInfo = matchedDoc.typeFact || matchedDoc;
            setTipoFactura(docInfo.id.toString());
            setForm((prev) => ({ ...prev, tipoDocumento: docInfo.id.toString() }));
          }
        }

        // 1.8. Extraer IVA si es Comprobante de Crédito Fiscal (03)
        let totalIva = 0;
        if (tipoDteStr === "03" && json.resumen?.tributos) {
          const tributoIva = json.resumen.tributos.find(t => t.codigo === "20");
          if (tributoIva) {
            totalIva = Number(tributoIva.valor) || 0;
          }
        }
        setIva(totalIva);

        // 2. Intentar buscar el proveedor por NIT o NRC
        const provOpts = await fetchProveedores();
        const nitEmisorStripped = nitEmisor?.replace(/-/g, ""); // Normalizer
        const nrcEmisorStr = json.emisor?.nrc;

        let matchedProvider = provOpts.find(p => {
          const matchNit = nitEmisor && (p.nit === nitEmisor || p.nit === nitEmisorStripped);
          const matchNrc = nrcEmisorStr && p.nrc === nrcEmisorStr;
          return matchNit || matchNrc;
        });

        if (matchedProvider) {
          // Temporal alert for debugging/user info:
          const matchedByNit = matchedProvider.nit === nitEmisor || matchedProvider.nit === nitEmisorStripped;
          const matchedByNrc = matchedProvider.nrc === nrcEmisorStr;

          Swal.fire({
            icon: 'info',
            title: 'Proveedor Encontrado Automáticamente',
            text: `El sistema ha seleccionado a "${matchedProvider.label}" porque tiene el mismo ${matchedByNit ? 'NIT' : 'NRC'} que el JSON subido.`,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 4500
          });

          setProveedorSeleccionado({ value: matchedProvider.value, label: matchedProvider.label });
        } else {
          // El proveedor NO existe. Pausamos la importación para registrarlo.
          const { depData, munData, actData } = await fetchProviderData(); // Carga de catálogos garantizada

          // Mapear Códigos de Hacienda a IDs del sistema
          const codDep = json.emisor?.direccion?.departamento;
          const codMun = json.emisor?.direccion?.municipio;
          const codAct = json.emisor?.codActividad;

          let idDep = "";
          let idMun = "";
          let idAct = "";

          const matchedDep = depData.find(d => d.codigo === codDep);
          if (matchedDep) idDep = matchedDep.id.toString();

          const matchedMun = munData.find(m => m.codigo === codMun);
          if (matchedMun) idMun = matchedMun.id.toString();

          const matchedAct = actData.find(a => a.codigo === codAct);
          if (matchedAct) idAct = matchedAct.id.toString();

          setFormProveedor(prev => ({
            ...prev,
            name: json.emisor?.nombre || "",
            nombre_comercial: json.emisor?.nombreComercial || json.emisor?.nombre || "",
            nit: nitEmisor || "",
            nrc: nrcEmisorStr || "",
            telefono: json.emisor?.telefono || "",
            correo: json.emisor?.correo || "",
            direccion: json.emisor?.direccion?.complemento || "",
            departamento: idDep,
            municipio: idMun,
            actividad: idAct
          }));

          // Guardamos el JSON pendiente para procesarlo después del registro
          setPendingJsonImport({ json, rawItems: json.cuerpoDocumento || [] });
          setShowProviderModal(true);
          e.target.value = null; // Limpiamos input
          return; // Detenemos la importación aquí
        }

        // Si el proveedor SÍ EXISTE, continuamos con el proceso normal
        await continueJsonProcessing(json, json.cuerpoDocumento || []);
        e.target.value = null;

      } catch (error) {
        console.error("Error parsing JSON", error);
        alert("Error al procesar el archivo JSON. Verifique el formato.");
      }
    };
    reader.readAsText(file);
  };

  const continueJsonProcessing = async (json, rawItems) => {
    // 3. Procesar items
    const mappedItems = [];

    // Asegurarnos de tener productos para matchear
    let currentAllProducts = allProducts;
    if (currentAllProducts.length === 0) {
      const res = await listProductos({});
      if (res && res.data) {
        currentAllProducts = res.data;
        setAllProducts(currentAllProducts);
      } else {
        console.warn("No se pudieron cargar los productos para matchear");
      }
    }

    for (const item of rawItems) {
      const jsonCode = item.codigo;

      let matched = null;
      if (jsonCode) {
        // A. Por SKU directo (comparación de strings para evitar fallos por tipos)
        matched = currentAllProducts.find(p => String(p.sku) === String(jsonCode));

        // B. Por ProductoRelacionados
        if (!matched) {
          matched = currentAllProducts.find(p =>
            p.ProductoRelacionados?.some(pr => String(pr.sku) === String(jsonCode))
          );
        }
      }

      if (matched) {
        mappedItems.push({
          value: matched.id,
          label: `${matched.sku} | ${matched.name}`,
          cantidad: item.cantidad || 1,
          precio: item.precioUni || 0,
          // Guardamos info del JSON por si el usuario quiere verificar
          jsonInfo: { codigo: jsonCode || "SIN CÓDIGO", descripcion: item.descripcion }
        });
      } else {
        // Si no hay match o no tiene código, agregamos un placeholder para vincular
        mappedItems.push({
          value: null,
          label: `⚠️ VINCULAR: ${jsonCode || "Sin código"} | ${item.descripcion}`,
          cantidad: item.cantidad || 1,
          precio: item.precioUni || 0,
          isPlaceholder: true,
          jsonInfo: { codigo: jsonCode || "SIN CÓDIGO", descripcion: item.descripcion }
        });
      }
    }

    setProductosSeleccionados(mappedItems);
    setShowModal(true);
  };

  const handleProveedorSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        name: formProveedor.name,
        nombre_comercial: formProveedor.nombre_comercial,
        nit: formProveedor.nit,
        dui: formProveedor.dui,
        nrc: formProveedor.nrc,
        id_actividad_economica: formProveedor.actividad,
        id_departamento: formProveedor.departamento,
        id_municipio: formProveedor.municipio,
        direccion: formProveedor.direccion,
        telefono: formProveedor.telefono,
        correo: formProveedor.correo,
        status: formProveedor.status,
      };

      const res = await createProveedor(data);

      // Actualizamos listado
      const newProvOpts = await fetchProveedores();

      // Seleccionamos el recién creado
      const newProvId = res?.id;
      if (newProvId) {
        const newlyCreated = newProvOpts.find(p => p.value === newProvId);
        if (newlyCreated) {
          setProveedorSeleccionado({ value: newlyCreated.value, label: newlyCreated.label });
        }
      }

      setShowProviderModal(false);

      // Retomamos el procesamiento del JSON si estaba pendiente
      if (pendingJsonImport) {
        await continueJsonProcessing(pendingJsonImport.json, pendingJsonImport.rawItems);
        setPendingJsonImport(null);
      }

    } catch (error) {
      console.error("Error al registrar proveedor desde importación", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateImportedProduct = (index, selectedProduct) => {
    if (!selectedProduct) return;
    const updated = [...productosSeleccionados];
    updated[index] = {
      ...updated[index],
      value: selectedProduct.id,
      label: `${selectedProduct.sku} | ${selectedProduct.name}`,
      isPlaceholder: false
    };
    setProductosSeleccionados(updated);
  };

  const opcionesTipoDocumento = (tipoDocumento || [])
    .filter((c) => {
      const doc = c.typeFact || c;
      return ["01", "03", "88"].includes(doc.codigo);
    })
    .map((c) => {
      const doc = c.typeFact || c;
      return {
        value: doc.id ? doc.id.toString() : "",
        label: doc.codigo ? `${doc.codigo} | ${doc.name}` : "Documento desconocido",
      };
    });

  const handleProcesarIngreso = async () => {
    setSubmitting(true);
    try {
      // Diferenciar IVA: JSON import lleva +13%, Manual queda tal cual
      const isJsonImport = !!rawJsonString;

      const detalleFinal = productosSeleccionados.map((p) => {
        const cant = Number(p.cantidad) || 0;
        const precUnitario = Number(p.precio) || 0;

        // Si es JSON agregamos IVA, si es manual lo dejamos tal cual
        const precioAplicado = isJsonImport ? (precUnitario * 1.13) : precUnitario;

        return {
          id_producto: p.value,
          cantidad: cant,
          precio: precioAplicado
        };
      });

      let totalIngreso = 0;
      if (isJsonImport) {
        try {
          const parsedJson = JSON.parse(rawJsonString);
          totalIngreso = Number(parsedJson?.resumen?.totalPagar) || 0;
        } catch (e) {
          totalIngreso = detalleFinal.reduce((acc, curr) => acc + (curr.cantidad * curr.precio), 0);
        }
      } else {
        totalIngreso = detalleFinal.reduce((acc, curr) => acc + (curr.cantidad * curr.precio), 0);
      }

      const ingresoData = {
        codigo_factura: codigoFactura,
        numero_factura: numeroFactura,
        id_proveedor: proveedorSeleccionado ? proveedorSeleccionado.value : null,
        fecha_ingreso: fechaIngreso,
        id_sucursal: 3,
        id_typeFacts: tipoFactura,
        ingresoDetalle: detalleFinal,
        total: totalIngreso,
        iva: iva,
        json: rawJsonString
      };

      // validando los datos de envio para proceder al ingreso
      if (DataIngreso(ingresoData)) {
        await AddIngresos(ingresoData);

        // Guardar mapeos de productos nuevos (JSON Import)
        const relacionados = productosSeleccionados
          .filter(p => !p.isPlaceholder && p.value && p.jsonInfo?.codigo && p.jsonInfo.codigo.trim() !== "" && p.jsonInfo.codigo !== "SIN CÓDIGO")
          .map(p => ({
            id_producto: p.value,
            codigo: p.jsonInfo.codigo,
            id_proveedor: proveedorSeleccionado ? proveedorSeleccionado.value : null,
            status: 1
          }));

        if (relacionados.length > 0) {
          try {
            await addProductoRelacionado(relacionados);
          } catch (err) {
            console.error("Error guardando productos relacionados importados", err);
          }
        }

        setShowModal(false);
        resetForm();
        await fetchData(1);
      }
    } catch (error) {
      console.error("Error al procesar el ingreso:", error);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Gestión de Ingresos</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Registre y administre las entradas de inventario</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileImport}
            accept=".json,application/json"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full sm:w-auto bg-white border border-slate-300 text-slate-700 px-4 py-2.5 rounded-lg hover:bg-slate-50 transition-colors shadow-sm font-medium flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Importar JSON
          </button>
          <button
            onClick={() => {
              resetForm();
              fetchProveedores();
              setShowModal(true);
            }}
            className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200 font-medium flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Ingreso
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Buscar ingreso por factura, proveedor..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-slate-50 focus:bg-white text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Mostrar:</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
          >
            {[10, 15, 25, 50, 100, 250, 500].map(num => (
              <option key={num} value={num}>
                {num} registros
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabla de ingresos */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10">
            <Loader />
          </div>
        )}

        {/* Tabla — solo md+ */}
        <div className="hidden md:block overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold">Fecha</th>
                <th className="px-4 py-3 font-semibold">Proveedor</th>
                <th className="px-4 py-3 font-semibold">Tipo de factura</th>
                <th className="px-4 py-3 font-semibold">Total Ingreso</th>
                <th className="px-4 py-3 font-semibold">Sucursal</th>
                <th className="px-4 py-3 font-semibold">Codigo Generacion</th>
                <th className="px-4 py-3 font-semibold">Numero Control</th>
                <th className="px-4 py-3 font-semibold text-center">Estado</th>
                <th className="px-4 py-3 font-semibold text-center sticky right-0 bg-slate-100 border-l border-slate-200 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Opciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ingresos.map((ingreso) => (
                <tr
                  key={ingreso.id}
                  className="hover:bg-indigo-50/50 transition-colors group"
                >
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDateReadable(ingreso.fecha_ingreso)}</td>
                  <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">{ingreso.Proveedore?.name}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {ingreso.TipoDocumento?.codigo} <br /> {ingreso.TipoDocumento?.name}
                  </td>
                  <td className="px-4 py-3 text-emerald-600 font-semibold whitespace-nowrap">${ingreso.total}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {ingreso.Sucursale?.codigo_sucursal} <br /> {ingreso.Sucursale?.nombre}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{ingreso.codigo_factura}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{ingreso.numero_factura}</td>
                  <td className="px-4 py-3 text-center">
                    <EstadoBadge status={ingreso.status} />
                  </td>
                  <td className="px-4 py-3 sticky right-0 bg-slate-100 group-hover:bg-slate-200 border-l border-slate-200 z-0 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.03)] transition-colors text-center">
                    <button
                      onClick={() => {
                        setSelectedIngreso(ingreso);
                        setShowDetailModal(true);
                      }}
                      className="bg-white border border-slate-200 text-indigo-600 px-3 py-1.5 rounded-lg hover:bg-indigo-50 hover:border-indigo-200 transition-all text-xs font-bold flex items-center gap-2 mx-auto"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Ver Detalle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards — solo móvil */}
        <div className="md:hidden divide-y divide-slate-100">
          {ingresos.map((ingreso) => (
            <div key={ingreso.id} className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{ingreso.Proveedore?.name || "Sin proveedor"}</p>
                  <p className="text-xs text-slate-500">{formatDateReadable(ingreso.fecha_ingreso)}</p>
                </div>
                <EstadoBadge status={ingreso.status} />
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                <div>
                  <span className="text-slate-400 font-medium uppercase tracking-wide">Total</span>
                  <p className="text-emerald-600 font-semibold">${ingreso.total}</p>
                </div>
                {ingreso.TipoDocumento?.name && (
                  <div>
                    <span className="text-slate-400 font-medium uppercase tracking-wide">Tipo</span>
                    <p className="text-slate-700 font-medium">{ingreso.TipoDocumento.codigo} - {ingreso.TipoDocumento.name}</p>
                  </div>
                )}
                {ingreso.numero_factura && (
                  <div className="col-span-2">
                    <span className="text-slate-400 font-medium uppercase tracking-wide">N° Control</span>
                    <p className="text-slate-700 font-medium font-mono text-[11px]">{ingreso.numero_factura}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  setSelectedIngreso(ingreso);
                  setShowDetailModal(true);
                }}
                className="w-full bg-white border border-slate-200 text-indigo-600 py-2 rounded-lg hover:bg-indigo-50 hover:border-indigo-200 transition-all text-xs font-bold flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Ver Detalle
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Paginación ingresos */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 sm:mt-6 px-1 gap-3">
        <p className="text-sm text-slate-500 text-center sm:text-left">
          Página <span className="font-medium">{page}</span> de <span className="font-medium">{totalPages}</span>
        </p>
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200 justify-center overflow-x-auto">
          <button
            disabled={page === 1}
            onClick={() => fetchData(page - 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === 1
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:bg-slate-100"
              }`}
          >
            ← Ant
          </button>

          {(() => {
            if (totalPages <= 7) {
              return Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => fetchData(num)}
                  className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num
                    ? "bg-indigo-50 text-indigo-600"
                    : "text-slate-600 hover:bg-slate-100"
                    }`}
                >
                  {num}
                </button>
              ));
            }

            // Para más de 7 páginas: mostrar 1 ... [page-1, page, page+1] ... N
            const items = [];
            const showLeftDots = page > 3;
            const showRightDots = page < totalPages - 2;

            items.push(
              <button key={1} onClick={() => fetchData(1)}
                className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === 1 ? "bg-indigo-50 text-indigo-600" : "text-slate-600 hover:bg-slate-100"}`}>
                1
              </button>
            );

            if (showLeftDots) {
              items.push(<span key="left-dots" className="px-2 py-1.5 text-slate-400">...</span>);
            }

            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);
            for (let num = start; num <= end; num++) {
              items.push(
                <button key={num} onClick={() => fetchData(num)}
                  className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num ? "bg-indigo-50 text-indigo-600" : "text-slate-600 hover:bg-slate-100"}`}>
                  {num}
                </button>
              );
            }

            if (showRightDots) {
              items.push(<span key="right-dots" className="px-2 py-1.5 text-slate-400">...</span>);
            }

            items.push(
              <button key={totalPages} onClick={() => fetchData(totalPages)}
                className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === totalPages ? "bg-indigo-50 text-indigo-600" : "text-slate-600 hover:bg-slate-100"}`}>
                {totalPages}
              </button>
            );

            return items;
          })()}

          <button
            disabled={page === totalPages}
            onClick={() => fetchData(page + 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === totalPages
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:bg-slate-100"
              }`}
          >
            Sig →
          </button>
        </div>
      </div>

      {/* Modal de Detalle - Simplificado */}
      {showDetailModal && selectedIngreso && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-start sm:items-center z-[70] p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden border border-slate-100 my-4 sm:my-0 max-h-[95vh] sm:max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-indigo-600 to-indigo-700 shrink-0">
              <div>
                <h3 className="text-base sm:text-lg font-black text-white tracking-tight">Detalle de Ingreso</h3>
                <p className="text-indigo-200 text-xs mt-0.5 font-medium">{selectedIngreso.numero_factura || "Sin referencia"}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl p-2 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Info Proveedor + Documento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:divide-x divide-slate-100 border-b border-slate-100 shrink-0">
              <div className="p-4 sm:p-5 border-b sm:border-b-0">
                <p className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-2">Proveedor</p>
                <p className="font-bold text-slate-800 text-sm leading-tight">{selectedIngreso.Proveedore?.name}</p>
                <div className="flex gap-4 mt-2">
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase font-bold">NIT</p>
                    <p className="text-xs text-slate-600 font-mono">{selectedIngreso.Proveedore?.nit || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-400 uppercase font-bold">NRC</p>
                    <p className="text-xs text-slate-600 font-mono">{selectedIngreso.Proveedore?.nrc || "—"}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <p className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-2">Documento</p>
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Tipo</span>
                    <span className="font-bold text-slate-700">{selectedIngreso.TipoDocumento?.name || "—"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Fecha</span>
                    <span className="font-bold text-slate-700">{formatDateReadable(selectedIngreso.fecha_ingreso)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla Productos */}
            <div className="overflow-y-auto custom-scrollbar flex-grow">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                  <tr>
                    <th className="px-5 py-3 text-slate-500 font-bold text-xs">Producto</th>
                    <th className="px-5 py-3 text-slate-500 font-bold text-xs text-center">Cant.</th>
                    <th className="px-5 py-3 text-slate-500 font-bold text-xs text-right">P. Unit</th>
                    <th className="px-5 py-3 text-slate-500 font-bold text-xs text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {selectedIngreso.IngresosDetalles?.map((det) => (
                    <tr key={det.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-slate-800 text-sm">{det.Producto?.name}</p>
                        <p className="text-[11px] text-slate-400 font-mono">SKU: {det.Producto?.sku}</p>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-lg text-xs font-bold">{det.cantidad}</span>
                      </td>
                      <td className="px-5 py-3.5 text-right text-slate-600 font-medium text-xs">${Number(det.precio || 0).toFixed(2)}</td>
                      <td className="px-5 py-3.5 text-right font-black text-slate-800 text-sm">${(det.cantidad * (det.precio || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer - Total + Cerrar */}
            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Total</p>
                <p className="text-2xl font-black text-indigo-600 tabular-nums">${Number(selectedIngreso.total || 0).toFixed(2)}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2.5 rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-indigo-100"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Modal Principal - Optimizado para móvil */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-0 sm:p-6 overflow-y-auto">
          <div className="bg-white shadow-2xl w-full h-full sm:h-auto sm:max-w-5xl sm:rounded-2xl flex flex-col sm:max-h-[90vh] overflow-hidden animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-800">
                  {editMode ? "Actualizar Ingreso" : "Nuevo Ingreso"}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 rounded-full p-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido principal con scroll unificado en móvil */}
            <div className="flex-grow overflow-y-auto custom-scrollbar bg-slate-50/30">
              <div className="flex flex-col md:flex-row min-h-full">
                {/* Sección izquierda: Detalles ingreso */}
                <div className="md:w-5/12 p-5 sm:p-6 border-b md:border-b-0 md:border-r border-slate-100">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <div className="col-span-2">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Proveedor</label>
                      <ReactSelect
                        options={proveedores}
                        value={proveedorSeleccionado}
                        onChange={setProveedorSeleccionado}
                        placeholder="Seleccione el proveedor"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
                            borderRadius: '0.8rem',
                            minHeight: '44px',
                            padding: '2px',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                          })
                        }}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Tipo de factura</label>
                      <ReactSelect
                        options={opcionesTipoDocumento}
                        value={opcionesTipoDocumento.find((opt) => opt.value === String(form.tipoDocumento)) || null}
                        onChange={(val) => handleSelectChange(val, "tipoDocumento")}
                        placeholder="Tipo de documento"
                        isDisabled={!!rawJsonString}
                        isSearchable
                        isClearable={!rawJsonString}
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
                            borderRadius: '0.8rem',
                            minHeight: '44px',
                            padding: '2px',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                          })
                        }}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Fecha Ingreso</label>
                      <input
                        type="date"
                        value={fechaIngreso}
                        onChange={(e) => setFechaIngreso(e.target.value)}
                        className="w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm h-[44px]"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Cód. Gen.</label>
                      <input
                        type="text"
                        value={codigoFactura}
                        onChange={(e) => setCodigoFactura(e.target.value)}
                        placeholder="Mandatorio"
                        className="w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm h-[44px]"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">No. Control</label>
                      <input
                        type="text"
                        value={numeroFactura}
                        onChange={(e) => setNumeroFactura(e.target.value)}
                        placeholder="Mandatorio"
                        className="w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm h-[44px]"
                      />
                    </div>
                  </div>
                </div>

                {/* Sección derecha: Productos */}
                <div className="md:w-7/12 p-5 sm:p-6 flex flex-col flex-grow">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="font-bold text-slate-800">Detalle de Ingreso</h4>
                    <button
                      className="bg-indigo-600 text-white font-medium px-4 py-2 rounded-xl hover:bg-indigo-700 transition-colors text-sm flex items-center gap-2 shadow-lg shadow-indigo-100"
                      onClick={() => setShowProductoModal(true)}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Agregar Producto
                    </button>
                  </div>

                  <div className="space-y-4">
                    {productosSeleccionados.length > 0 ? (
                      <div className="space-y-3">
                        {productosSeleccionados.map((prod, i) => (
                          <div key={prod.value ? `v-${prod.value}` : `i-${i}`} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm group relative">
                            <button
                              onClick={() => {
                                const updated = [...productosSeleccionados];
                                updated.splice(i, 1);
                                setProductosSeleccionados(updated);
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <div className="flex flex-col gap-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <span className={`font-bold text-sm leading-tight ${prod.isPlaceholder ? "text-amber-600" : "text-slate-800"}`}>
                                  {prod.label}
                                </span>
                                {prod.jsonInfo && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
                                    JSON: {prod.jsonInfo.codigo} - {prod.jsonInfo.descripcion.substring(0, 30)}...
                                  </span>
                                )}
                              </div>

                              {prod.jsonInfo && (
                                <div className="mt-1">
                                  <label className="text-[10px] uppercase font-bold text-indigo-500 mb-1 block">
                                    Vincular con mi producto:
                                  </label>
                                  <ReactSelect
                                    options={allProducts.map(p => ({ value: p.id, label: `${p.sku} | ${p.name}`, original: p }))}
                                    value={prod.value ? { value: prod.value, label: prod.label } : null}
                                    onChange={(val) => handleUpdateImportedProduct(i, val?.original)}
                                    placeholder="Buscar producto coincidente..."
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                                      control: (baseStyles) => ({
                                        ...baseStyles,
                                        borderRadius: '0.5rem',
                                        fontSize: '12px',
                                        minHeight: '32px'
                                      })
                                    }}
                                  />
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Cantidad</label>
                                  <input
                                    type="number"
                                    value={prod.cantidad}
                                    onChange={(e) => handleChangeCantidad(i, e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Precio Costo</label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                                    <input
                                      type="number"
                                      value={prod.precio}
                                      onChange={(e) => handleChangePrecio(i, e.target.value)}
                                      className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-12 flex flex-col items-center justify-center p-8 text-center bg-white rounded-2xl border border-dashed border-slate-300">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <p className="text-slate-500 font-bold">Sin productos</p>
                        <p className="text-xs text-slate-400 mt-1">Haga clic en 'Agregar Producto' para comenzar.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer dentro del scroll en móvil */}
              <div className="md:hidden p-5 border-t border-slate-200 bg-white flex flex-col gap-3 mt-auto">
                <div className="flex justify-between items-center bg-indigo-50 p-4 rounded-xl border border-indigo-100 mb-2">
                  <span className="text-indigo-600 font-black uppercase text-[10px] tracking-widest">
                    {rawJsonString ? "Total (con IVA)" : "Total Final"}
                  </span>
                  <div className="flex flex-col items-end">
                    <span className="text-indigo-700 font-black text-xl">
                      ${(() => {
                        if (rawJsonString) {
                          try {
                            const parsedJson = JSON.parse(rawJsonString);
                            if (parsedJson?.resumen?.totalPagar !== undefined) {
                              return Number(parsedJson.resumen.totalPagar).toFixed(2);
                            }
                          } catch (e) { }
                        }
                        return productosSeleccionados.reduce((acc, p) => {
                          const base = (Number(p.cantidad) || 0) * (Number(p.precio) || 0);
                          return acc + (rawJsonString ? base * 1.13 : base);
                        }, 0).toFixed(2);
                      })()}
                    </span>
                    {iva > 0 && (
                      <span className="text-indigo-400 font-bold text-[10px] uppercase">
                        (Incluye IVA: ${iva.toFixed(2)})
                      </span>
                    )}
                  </div>
                </div>
                <button
                  disabled={submitting || productosSeleccionados.length === 0}
                  onClick={handleProcesarIngreso}
                  className={`w-full py-3.5 rounded-xl flex items-center justify-center gap-3 font-bold shadow-lg shadow-indigo-100 transition-all text-white text-sm uppercase tracking-wide ${submitting || productosSeleccionados.length === 0
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 active:scale-[0.98]"
                    }`}
                >
                  {submitting && (
                    <span className="inline-block w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></span>
                  )}
                  {editMode ? "Actualizar Ingreso" : "Generar Ingreso"}
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="w-full py-3.5 rounded-xl border border-slate-300 text-slate-700 font-bold active:scale-[0.98] transition-all text-sm uppercase tracking-wide"
                >
                  Cancelar
                </button>
              </div>
            </div>

            {/* Footer Desktop */}
            <div className="hidden md:flex p-4 sm:p-6 border-t border-slate-200 bg-white items-center justify-between shrink-0">
              <div className="flex items-center gap-4 bg-indigo-50 px-6 py-3 rounded-2xl border border-indigo-100 shadow-sm">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-black text-indigo-400 tracking-widest leading-none mb-1">
                    {rawJsonString ? "Total Estimado (con IVA)" : "Total a Pagar"}
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-indigo-600 font-extrabold text-2xl leading-none">
                      ${(() => {
                        if (rawJsonString) {
                          try {
                            const parsedJson = JSON.parse(rawJsonString);
                            if (parsedJson?.resumen?.totalPagar !== undefined) {
                              return Number(parsedJson.resumen.totalPagar).toFixed(2);
                            }
                          } catch (e) { }
                        }
                        return productosSeleccionados.reduce((acc, p) => {
                          const base = (Number(p.cantidad) || 0) * (Number(p.precio) || 0);
                          return acc + (rawJsonString ? base * 1.13 : base);
                        }, 0).toFixed(2);
                      })()}
                    </span>
                    {iva > 0 && (
                      <span className="text-indigo-400 font-bold text-[10px] uppercase tracking-wider">
                        Incluye IVA: ${iva.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-8 w-px bg-indigo-200/50 mx-2"></div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-slate-400 leading-none mb-1">Productos</span>
                  <span className="text-slate-700 font-bold text-lg leading-none">{productosSeleccionados.length}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-8 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 transition-all text-sm uppercase tracking-wide"
                >
                  Cancelar
                </button>
                <button
                  disabled={submitting || productosSeleccionados.length === 0}
                  onClick={handleProcesarIngreso}
                  className={`px-10 py-3 rounded-xl flex items-center justify-center gap-3 font-bold shadow-lg shadow-indigo-100 transition-all text-white text-sm uppercase tracking-wide ${submitting || productosSeleccionados.length === 0
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-0.5"
                    }`}
                >
                  {submitting && (
                    <span className="inline-block w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></span>
                  )}
                  {editMode ? "Actualizar Ingreso" : "Guardar Ingreso"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )
      }

      {/* Modal de Productos */}
      {
        showProductoModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start sm:items-center z-[60] p-2 sm:p-6 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col my-4 sm:my-8 animate-fade-in-up max-h-[95vh] sm:max-h-[90vh]">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-slate-800">Seleccionar Productos</h3>
                </div>
                <button
                  onClick={() => setShowProductoModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 rounded-full p-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar flex-grow flex flex-col">
                <div className="relative mb-6">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar producto por SKU, nombre..."
                    value={buscarProducto}
                    onChange={(e) => {
                      setBuscarProducto(e.target.value);
                      setPagePro(1);
                    }}
                    className="pl-10 pr-4 py-2.5 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-slate-50 focus:bg-white"
                  />
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex-grow flex flex-col">
                  <div className="overflow-x-auto custom-scrollbar flex-grow">
                    <table className="w-full text-sm text-left border-collapse">
                      <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 font-semibold">SKU</th>
                          <th className="px-4 py-3 font-semibold">Nombre</th>
                          <th className="px-4 py-3 font-semibold">Precio / U.</th>
                          <th className="px-4 py-3 font-semibold text-center">Existencia</th>
                          <th className="px-4 py-3 font-semibold text-center w-28">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {productos.length > 0 ? (
                          productos.map((prod) => {
                            const isAdded = productosSeleccionados.some(p => p.value === prod.id);
                            const existencia = prod?.ExistenciaProductos?.[0]?.stock ?? 0;
                            return (
                              <tr key={prod.id} className="hover:bg-indigo-50/50 transition-colors">
                                <td className="px-4 py-3 font-medium text-slate-700">{prod.sku}</td>
                                <td className="px-4 py-3 text-slate-600">{prod.name}</td>
                                <td className="px-4 py-3 text-emerald-600 font-medium">${prod.price}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${existencia > 0
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                    : "bg-rose-50 text-rose-800 border-rose-200"
                                    }`}>
                                    {existencia}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors w-full flex items-center justify-center gap-1 ${isAdded
                                      ? "bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-default"
                                      : "bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 hover:text-indigo-700"
                                      }`}
                                    onClick={() => !isAdded && handleAddProducto(prod)}
                                    disabled={isAdded}
                                  >
                                    {isAdded ? (
                                      <>
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Agregado
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Agregar
                                      </>
                                    )}
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                              No se encontraron productos con el criterio de búsqueda.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginación productos (dentro de la tabla) */}
                  {totalPagesPro > 1 && (
                    <div className="flex justify-between items-center p-3 border-t border-slate-200 bg-slate-50 mt-auto">
                      <p className="text-xs text-slate-500 hidden sm:block">
                        Página <span className="font-medium">{pagePro}</span> de <span className="font-medium">{totalPagesPro}</span>
                      </p>
                      <div className="flex gap-1 justify-center w-full sm:w-auto">
                        <button
                          disabled={pagePro === 1}
                          onClick={() => setPagePro(pagePro - 1)}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${pagePro === 1
                            ? "text-slate-400 cursor-not-allowed bg-transparent"
                            : "text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 shadow-sm"
                            }`}
                        >
                          Anterior
                        </button>
                        <div className="flex bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
                          {Array.from({ length: totalPagesPro }, (_, i) => i + 1)
                            .filter(num => num === 1 || num === totalPagesPro || (num >= pagePro - 1 && num <= pagePro + 1))
                            .map((num, i, arr) => {
                              if (i > 0 && arr[i - 1] !== num - 1) {
                                return (
                                  <span key={`dots-${num}`} className="px-2 py-1 text-xs text-slate-400 border-l border-slate-200 first:border-l-0 bg-slate-50">
                                    ...
                                  </span>
                                );
                              }
                              return (
                                <button
                                  key={num}
                                  onClick={() => setPagePro(num)}
                                  className={`min-w-[28px] px-2 py-1 text-xs font-medium transition-colors border-l border-slate-200 first:border-l-0 ${pagePro === num
                                    ? "bg-indigo-50 text-indigo-600"
                                    : "text-slate-600 hover:bg-slate-50"
                                    }`}
                                >
                                  {num}
                                </button>
                              );
                            })}
                        </div>
                        <button
                          disabled={pagePro === totalPagesPro}
                          onClick={() => setPagePro(pagePro + 1)}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${pagePro === totalPagesPro
                            ? "text-slate-400 cursor-not-allowed bg-transparent"
                            : "text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 shadow-sm"
                            }`}
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end rounded-b-2xl shrink-0">
                <button
                  className="px-5 py-2.5 rounded-lg font-medium transition-all text-white bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200 shadow-sm"
                  onClick={() => setShowProductoModal(false)}
                >
                  Cerrar y Volver
                </button>
              </div>
            </div>
          </div>
        )}
      {showProviderModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start sm:items-center z-[60] p-2 sm:p-6 overflow-y-auto w-full max-w-full">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh]">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base sm:text-xl font-bold text-slate-800">Proveedor No Encontrado</h3>
                  <p className="text-xs sm:text-sm text-slate-500">Por favor, registre el proveedor para continuar con la importación.</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowProviderModal(false);
                  setPendingJsonImport(null);
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 rounded-full p-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex-grow bg-slate-50/30">
              <form onSubmit={handleProveedorSubmit} id="import-proveedor-form" className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Representante</label>
                    <input
                      type="text"
                      value={formProveedor.name}
                      onChange={(e) => setFormProveedor(p => ({ ...p, name: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Nombre Comercial</label>
                    <input
                      type="text"
                      value={formProveedor.nombre_comercial}
                      onChange={(e) => setFormProveedor(p => ({ ...p, nombre_comercial: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">NRC</label>
                    <input
                      type="text"
                      value={formProveedor.nrc}
                      onChange={(e) => setFormProveedor(p => ({ ...p, nrc: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">NIT</label>
                    <input
                      type="text"
                      value={formProveedor.nit}
                      onChange={(e) => setFormProveedor(p => ({ ...p, nit: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">DUI</label>
                    <input
                      type="text"
                      value={formProveedor.dui}
                      onChange={(e) => setFormProveedor(p => ({ ...p, dui: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Teléfono</label>
                    <input
                      type="text"
                      value={formProveedor.telefono}
                      onChange={(e) => setFormProveedor(p => ({ ...p, telefono: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Correo Electrónico</label>
                    <input
                      type="email"
                      value={formProveedor.correo}
                      onChange={(e) => setFormProveedor(p => ({ ...p, correo: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                    <hr className="border-slate-100 my-2" />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Departamento</label>
                    <ReactSelect
                      options={departamentos.map(d => ({ value: d.id.toString(), label: `${d.codigo} | ${d.name}` }))}
                      value={departamentos.map(d => ({ value: d.id.toString(), label: `${d.codigo} | ${d.name}` })).find(opt => opt.value === formProveedor.departamento) || null}
                      onChange={(val) => setFormProveedor(p => ({ ...p, departamento: val?.value || "" }))}
                      placeholder="Seleccionar..."
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Municipio</label>
                    <ReactSelect
                      options={municipios.map(m => ({ value: m.id.toString(), label: `${m.codigo} | ${m.name}` }))}
                      value={municipios.map(m => ({ value: m.id.toString(), label: `${m.codigo} | ${m.name}` })).find(opt => opt.value === formProveedor.municipio) || null}
                      onChange={(val) => setFormProveedor(p => ({ ...p, municipio: val?.value || "" }))}
                      placeholder="Seleccionar..."
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Actividad Económica</label>
                    <ReactSelect
                      options={actividad.map(a => ({ value: a.id.toString(), label: `${a.codigo} | ${a.name}` }))}
                      value={actividad.map(a => ({ value: a.id.toString(), label: `${a.codigo} | ${a.name}` })).find(opt => opt.value === formProveedor.actividad) || null}
                      onChange={(val) => setFormProveedor(p => ({ ...p, actividad: val?.value || "" }))}
                      placeholder="Seleccionar..."
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                    <label className="block mb-2 text-sm font-semibold text-slate-700">Dirección Exacta</label>
                    <textarea
                      value={formProveedor.direccion}
                      onChange={(e) => setFormProveedor(p => ({ ...p, direccion: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px] text-sm"
                      required
                    ></textarea>
                  </div>
                </div>
              </form>
            </div>

            <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 rounded-b-2xl shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowProviderModal(false);
                  setPendingJsonImport(null);
                }}
                className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white transition-colors text-sm"
                disabled={submitting}
              >
                Cancelar Importación
              </button>
              <button
                type="submit"
                form="import-proveedor-form"
                disabled={submitting}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium shadow-sm flex items-center justify-center gap-2 text-sm"
              >
                {submitting ? "Guardando..." : "Guardar y Continuar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default Ingresos;
